import {
  faqItems,
  featureColumnOne,
  featureColumnTwo,
} from "../app/landing/landingContent";
import pricingTiersData from "../app/landing/pricingTiers.json";
import {
  SECURITY_NAV,
  SECURITY_PAGE_SUBTITLE,
  getSecuritySectionById,
} from "../app/landing/securityContent";
import {
  TOOLKIT_NAV,
  TOOLKIT_PAGE_SUBTITLE,
  getToolkitFeatureCount,
  getToolkitSectionById,
  toolkitFaqItems,
  toolkitSections,
} from "../app/landing/toolkitContent";

const SITE_ORIGIN = "https://www.emailperfect.io";
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

function toolkitSectionUrl(slug: string): string {
  return slug === "overview" ? "/toolkit" : `/toolkit/${slug}`;
}

function securitySectionUrl(slug: string): string {
  return slug === "overview" ? "/security" : `/security/${slug}`;
}

const ALL_FEATURES = [...featureColumnOne, ...featureColumnTwo];

type MetaInput = {
  title: string;
  description: string;
  canonical: string;
  ogType?: string;
};

function upsertMeta(
  key: string,
  content: string,
  attr: "name" | "property" = "name",
): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function replaceJsonLd(id: string, data: unknown): void {
  document.getElementById(id)?.remove();
  const el = document.createElement("script");
  el.id = id;
  el.type = "application/ld+json";
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
}

export function setPageMeta(input: MetaInput): void {
  document.title = input.title;
  upsertMeta("description", input.description);
  upsertLink("canonical", input.canonical);
  upsertMeta("og:title", input.title, "property");
  upsertMeta("og:description", input.description, "property");
  upsertMeta("og:url", input.canonical, "property");
  upsertMeta("og:type", input.ogType ?? "website", "property");
  upsertMeta("twitter:title", input.title);
  upsertMeta("twitter:description", input.description);
}

function faqSchema(questions: Array<{ question: string; answer: string }>) {
  return {
    "@type": "FAQPage",
    mainEntity: questions.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}

function breadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

function pricingOfferCatalogSchema() {
  const tiers = pricingTiersData.tiers.filter((tier) => tier.id !== "enterprise");
  return {
    "@type": "Product",
    name: "Email Perfect",
    description:
      "Collaborative visual email builder with preflight checks, team workflows, and production HTML export.",
    brand: { "@id": ORG_ID },
    url: `${SITE_ORIGIN}/pricing`,
    offers: tiers.map((tier) => ({
      "@type": "Offer",
      name: `${tier.name} plan`,
      description: tier.tagline,
      price: tier.priceMonthly ?? 0,
      priceCurrency: "USD",
      url: `${SITE_ORIGIN}/pricing`,
      availability: "https://schema.org/InStock",
    })),
  };
}

export function applyHomeGeoSeo(): void {
  setPageMeta({
    title: "Email Perfect — Build better emails, faster",
    description:
      "Email Perfect is a collaborative visual email builder for marketing and transactional HTML. Design with Figma-like freedom, run preflight checks, collaborate with comments, and export client-safe HTML to any ESP—no hand-coding required.",
    canonical: `${SITE_ORIGIN}/`,
  });

  replaceJsonLd("geo-page-jsonld", {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE_ORIGIN}/#webpage`,
        url: `${SITE_ORIGIN}/`,
        name: "Email Perfect — Build better emails, faster",
        description:
          "Design, collaborate on, and export production-ready marketing email HTML with visual editing, preflight validation, and ESP-compatible output.",
        isPartOf: { "@id": WEBSITE_ID },
        about: { "@id": `${SITE_ORIGIN}/#webapp` },
        inLanguage: "en-US",
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["#geo-home-summary", "h1"],
        },
      },
      faqSchema(faqItems),
      {
        "@type": "ItemList",
        name: "Email Perfect core capabilities",
        itemListElement: ALL_FEATURES.map((feature, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: feature.title,
          description: feature.body,
        })),
      },
      {
        "@type": "HowTo",
        name: "How to build an email in Email Perfect",
        description:
          "Create a production-ready marketing email without writing HTML by hand.",
        step: [
          {
            "@type": "HowToStep",
            position: 1,
            name: "Create a project",
            text: "Sign in and start a new email project from the dashboard.",
          },
          {
            "@type": "HowToStep",
            position: 2,
            name: "Design visually",
            text: "Lay out sections, typography, images, and components in the visual editor.",
          },
          {
            "@type": "HowToStep",
            position: 3,
            name: "Review and preflight",
            text: "Preview across devices, run accessibility and spam checks, and resolve warnings.",
          },
          {
            "@type": "HowToStep",
            position: 4,
            name: "Export HTML",
            text: "Copy or download HTML that works across major email clients and import it into your ESP.",
          },
        ],
      },
    ],
  });
}

export function applyPricingGeoSeo(): void {
  setPageMeta({
    title: "Email Perfect — Pricing",
    description:
      "Email Perfect pricing: Basic (free), Plus ($19/mo), Pro ($49/mo), and Enterprise. Compare AI credits, preflight checks, collaboration seats, version history, and export limits.",
    canonical: `${SITE_ORIGIN}/pricing`,
  });

  replaceJsonLd("geo-page-jsonld", {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE_ORIGIN}/pricing#webpage`,
        url: `${SITE_ORIGIN}/pricing`,
        name: "Email Perfect Pricing",
        description: pricingTiersData.subheadline,
        isPartOf: { "@id": WEBSITE_ID },
        inLanguage: "en-US",
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["#geo-pricing-summary", "#pricing-page-headline"],
        },
      },
      breadcrumbSchema([
        { name: "Home", url: `${SITE_ORIGIN}/` },
        { name: "Pricing", url: `${SITE_ORIGIN}/pricing` },
      ]),
      pricingOfferCatalogSchema(),
      faqSchema(faqItems),
    ],
  });
}

export function applyToolkitGeoSeo(slug: string): void {
  const section = getToolkitSectionById(slug);
  const title =
    slug === "overview"
      ? "Email Perfect — Toolkit"
      : slug === "faq"
        ? "FAQ — Email Perfect Toolkit"
        : `${section?.title ?? "Toolkit"} — Email Perfect Toolkit`;

  const description =
    slug === "overview"
      ? `${TOOLKIT_PAGE_SUBTITLE} Documentation for ${getToolkitFeatureCount()} product features including the visual editor, preflight, collaboration, export, and AI assistance.`
      : slug === "faq"
        ? "Frequently asked questions about Email Perfect features, plans, exports, collaboration, and email client compatibility."
        : section
          ? `${section.description} Learn why it matters, how to use it, and pro tips from the Email Perfect Toolkit.`
          : TOOLKIT_PAGE_SUBTITLE;

  const canonical = `${SITE_ORIGIN}${toolkitSectionUrl(slug)}`;

  setPageMeta({
    title,
    description,
    canonical,
    ogType: slug === "overview" || slug === "faq" ? "website" : "article",
  });

  const toolkitArticles = toolkitSections
    .filter((s) => !s.isFaq)
    .map((s) => ({
      "@type": "TechArticle",
      "@id": `${SITE_ORIGIN}${toolkitSectionUrl(s.id)}#article`,
      headline: s.title,
      description: s.description,
      url: `${SITE_ORIGIN}${toolkitSectionUrl(s.id)}`,
      author: { "@id": ORG_ID },
      publisher: { "@id": ORG_ID },
      inLanguage: "en-US",
      isPartOf: { "@id": `${SITE_ORIGIN}/toolkit#collection` },
    }));

  const graph: unknown[] = [
    {
      "@type": "CollectionPage",
      "@id": `${SITE_ORIGIN}/toolkit#collection`,
      url: `${SITE_ORIGIN}/toolkit`,
      name: "Email Perfect Toolkit",
      description: TOOLKIT_PAGE_SUBTITLE,
      isPartOf: { "@id": WEBSITE_ID },
      inLanguage: "en-US",
      hasPart: toolkitArticles.map((a) => ({ "@id": (a as { "@id": string })["@id"] })),
    },
    breadcrumbSchema([
      { name: "Home", url: `${SITE_ORIGIN}/` },
      { name: "Toolkit", url: `${SITE_ORIGIN}/toolkit` },
      ...(slug !== "overview"
        ? [{ name: section?.title ?? "Section", url: canonical }]
        : []),
    ]),
    {
      "@type": "ItemList",
      name: "Email Perfect Toolkit sections",
      itemListElement: TOOLKIT_NAV.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.label,
        url: `${SITE_ORIGIN}${toolkitSectionUrl(item.id)}`,
      })),
    },
    faqSchema(toolkitFaqItems),
  ];

  if (section && !section.isFaq && !section.isOverview) {
    graph.push({
      "@type": "TechArticle",
      "@id": `${canonical}#article`,
      headline: section.title,
      description: section.description,
      url: canonical,
      author: { "@id": ORG_ID },
      publisher: { "@id": ORG_ID },
      inLanguage: "en-US",
      articleBody: [
        section.description,
        section.whyItMatters,
        ...section.howToUse,
        ...section.proTips,
        ...(section.subsections?.map((sub) => `${sub.title}. ${sub.body}`) ?? []),
      ].join(" "),
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [`#${slug} h2`, `#${slug} .toolkit-section-lead`],
      },
    });
  }

  replaceJsonLd("geo-page-jsonld", {
    "@context": "https://schema.org",
    "@graph": graph,
  });
}

export function applySecurityGeoSeo(slug: string): void {
  const section = getSecuritySectionById(slug);
  const title =
    slug === "overview"
      ? "Email Perfect — Security"
      : `${section?.title ?? "Security"} — Email Perfect Security`;

  const description =
    slug === "overview"
      ? `${SECURITY_PAGE_SUBTITLE} Learn about hosting, encryption, authentication, cookies, subprocessors, and how to contact us.`
      : section
        ? section.lead
        : SECURITY_PAGE_SUBTITLE;

  const canonical = `${SITE_ORIGIN}${securitySectionUrl(slug)}`;

  setPageMeta({
    title,
    description,
    canonical,
    ogType: slug === "overview" ? "website" : "article",
  });

  replaceJsonLd("geo-page-jsonld", {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE_ORIGIN}/security#webpage`,
        url: `${SITE_ORIGIN}/security`,
        name: "Email Perfect Security",
        description: SECURITY_PAGE_SUBTITLE,
        isPartOf: { "@id": WEBSITE_ID },
        inLanguage: "en-US",
      },
      breadcrumbSchema([
        { name: "Home", url: `${SITE_ORIGIN}/` },
        { name: "Security", url: `${SITE_ORIGIN}/security` },
        ...(slug !== "overview"
          ? [{ name: section?.title ?? "Section", url: canonical }]
          : []),
      ]),
      {
        "@type": "ItemList",
        name: "Email Perfect Security sections",
        itemListElement: SECURITY_NAV.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.label,
          url: `${SITE_ORIGIN}${securitySectionUrl(item.id)}`,
        })),
      },
    ],
  });
}

const CHANGELOG_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

export function formatChangelogWeekLabel(weekKey: string, today = new Date()): string {
  const mon = parseIsoDate(weekKey);
  const fri = new Date(mon);
  fri.setDate(fri.getDate() + 4);

  const todayNoon = new Date(today);
  todayNoon.setHours(12, 0, 0, 0);

  const day = todayNoon.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monWeek = new Date(todayNoon);
  monWeek.setDate(monWeek.getDate() + diff);

  const isCurrentWeek = mon.getTime() === monWeek.getTime();
  const end = isCurrentWeek ? todayNoon : fri;
  const endYear = end.getFullYear();

  const fmt = (d: Date) => `${CHANGELOG_MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;

  if (mon.getMonth() === end.getMonth()) {
    return `${fmt(mon)} – ${end.getDate()}, ${endYear}`;
  }
  return `${fmt(mon)} – ${fmt(end)}, ${endYear}`;
}

type ChangelogEntryForSeo = {
  id: string;
  date: string;
  title: string;
  summary: string;
};

export function applyChangelogGeoSeo(entries: ChangelogEntryForSeo[]): void {
  const description =
    "Developer changelog for Email Perfect — visual email editor updates, collaboration, export, preflight, projects, account, and API changes.";

  setPageMeta({
    title: "Developer changelog — Email Perfect",
    description,
    canonical: `${SITE_ORIGIN}/changelog`,
  });

  const graph: unknown[] = [
    {
      "@type": "WebPage",
      "@id": `${SITE_ORIGIN}/changelog#webpage`,
      url: `${SITE_ORIGIN}/changelog`,
      name: "Email Perfect Developer Changelog",
      description,
      isPartOf: { "@id": WEBSITE_ID },
      inLanguage: "en-US",
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["#geo-changelog-summary"],
      },
    },
    breadcrumbSchema([
      { name: "Home", url: `${SITE_ORIGIN}/` },
      { name: "Changelog", url: `${SITE_ORIGIN}/changelog` },
    ]),
    {
      "@type": "ItemList",
      name: "Email Perfect developer changelog",
      numberOfItems: entries.length,
      itemListElement: entries.map((entry, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "BlogPosting",
          "@id": `${SITE_ORIGIN}/changelog#${entry.id}`,
          headline: entry.title,
          datePublished: entry.date,
          description: entry.summary,
          url: `${SITE_ORIGIN}/changelog#${entry.id}`,
          author: { "@id": ORG_ID },
          publisher: { "@id": ORG_ID },
          inLanguage: "en-US",
        },
      })),
    },
  ];

  replaceJsonLd("geo-page-jsonld", {
    "@context": "https://schema.org",
    "@graph": graph,
  });
}
